import { FC, useEffect } from 'react'
import { Navigate, matchPath, useLocation, useParams } from 'react-router-dom'

import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { Path } from 'constants/router'
import { PurchaseFlow } from 'components/pages/PurchaseFlow'
import { PurchaseFlowRevamp } from 'components/pages/PurchaseFlowRevamp'
import { FeatureFlag, useFeatureFlag, useFeatureFlagsReadyFor } from 'utils/featureFlags'
import { useFlagUserId } from 'utils/featureFlags/useFlagUserId'
import { logger } from 'utils/logging'

/** Query parameters only legacy reads: a link carrying one keeps opening legacy while PF v2 cannot start from it. */
const LEGACY_ONLY_PARAMS = ['realtyId']

/** Why a purchase-flow URL was sent somewhere other than where it points. */
type FlowRedirectReason = 'flag_off' | 'legacy_only_param' | 'former_revamp_path'

/** Logs a redirect once per URL, so a flag switch or a lingering old link shows up in the logs. */
const useLogFlowRedirect = (reason: FlowRedirectReason | null, pathname: string, search: string) => {
  useEffect(() => {
    if (!reason) return
    const context = { reason, pathname, hasPaymentIntent: new URLSearchParams(search).has('payment_intent') }
    // A PF v2 step sent to legacy loses the client's place (a Stripe return included).
    if (reason === 'flag_off') logger.warn('Purchase flow: PF v2 URL sent to legacy', context)
    else logger.info('Purchase flow: URL redirected', context)
  }, [reason, pathname, search])
}

/** Decides only once the flags are final for the signed-in user, so nobody sees one flow flash before the other. */
const useAllowPfRevamp = (): boolean | undefined => {
  const flagsReady = useFeatureFlagsReadyFor(useFlagUserId())
  const allowPfRevamp = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP)
  return flagsReady ? allowPfRevamp : undefined
}

/**
 * `/purchase-flow/*`: PF v2 while `allow-pf-revamp` is on, legacy while it is off.
 * Legacy has a single URL, so with the flag off a PF v2 step path lands on it.
 */
export const PurchaseFlowSwitch: FC = () => {
  const allowPfRevamp = useAllowPfRevamp()
  const { pathname, search, hash, state } = useLocation()
  const isEntry = matchPath(Path.PURCHASE_FLOW, pathname) !== null
  const hasLegacyOnlyParam = LEGACY_ONLY_PARAMS.some((param) => new URLSearchParams(search).has(param))
  const redirectReason: FlowRedirectReason | null =
    allowPfRevamp === false && !isEntry ? 'flag_off'
      : allowPfRevamp === true && isEntry && hasLegacyOnlyParam ? 'legacy_only_param'
        : null
  useLogFlowRedirect(redirectReason, pathname, search)

  if (allowPfRevamp === undefined) return <LoadingPage />

  if (!allowPfRevamp) {
    return isEntry
      ? <PurchaseFlow />
      : <Navigate to={{ pathname: Path.PURCHASE_FLOW, search, hash }} state={state} replace />
  }

  if (isEntry && hasLegacyOnlyParam) {
    return <Navigate to={{ pathname: Path.PURCHASE_FLOW_LEGACY, search, hash }} state={state} replace />
  }

  return <PurchaseFlowRevamp />
}

/** `/purchase-flow-legacy`: legacy while the flag is on; with the flag off legacy is `/purchase-flow` itself. */
export const LegacyPurchaseFlowRoute: FC = () => {
  const allowPfRevamp = useAllowPfRevamp()
  const { search, hash, state } = useLocation()

  if (allowPfRevamp === undefined) return <LoadingPage />
  if (!allowPfRevamp) return <Navigate to={{ pathname: Path.PURCHASE_FLOW, search, hash }} state={state} replace />
  return <PurchaseFlow />
}

/** `/purchase-flow-revamp/*`, PF v2's former mount point: the same step under `/purchase-flow`. */
export const FormerPurchaseFlowRevampRedirect: FC = () => {
  const { '*': rest } = useParams()
  const { pathname: formerPathname, search, hash, state } = useLocation()
  const pathname = rest ? `${Path.PURCHASE_FLOW}/${rest}` : Path.PURCHASE_FLOW
  useLogFlowRedirect('former_revamp_path', formerPathname, search)
  return <Navigate to={{ pathname, search, hash }} state={state} replace />
}
